//*Import libraries from react
import { useEffect, useState } from "react";

//*Import components from react-bootstrap
import { Button, Form, Spinner, Alert } from "react-bootstrap";

//*Import components
import { Attention } from "../../../component/base/Attention";

//*Import hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//*Import icons from material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//*Import custom hooks
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";

export function DisciplinesForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [originalData, setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [nameError, setNameError] = useState<string>("");

  const {
    getDisciplineById,
    createDisciplines,
    updateDisciplines,
    isCreating,
    isUpdating,
    error,
  } = useDisciplines(token);

  // Func to validate the name field
  const validateName = (name: string): boolean => {
    if (name.trim().length < 3) {
      setNameError("O nome deve ter pelo menos 3 caracteres.");
      return false;
    } else {
      setNameError("");
      return true;
    }
  };

  // Fetch disciplines data in edit mode
  useEffect(() => {
    const fetchDisciplinesData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const disciplinesData = await getDisciplineById(params.id);
          if (disciplinesData) {
            const newFormData = {
              name: disciplinesData.name || "",
            };
            setFormData(newFormData);
            setOriginalData(disciplinesData);
          }
        } catch (error) {
          console.error("Erro ao buscar dados do disciplina:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchDisciplinesData();
  }, [editMode, params.id, getDisciplineById]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const isNameValid = validateName(formData.name);
    if (!isNameValid) {
      return;
    }

    try {
      if (editMode && params.id) {
        await updateDisciplines({
          id: params.id,
          disciplineData: {
            name: formData.name,
          },
        });
        navigate("/disciplines");
      } else {
        await createDisciplines({
          name: formData.name,
        });
        navigate("/disciplines");
      }
    } catch (err) {
      console.error("Operação falhou:", err);
    }
  };

  const isLoading = isCreating || isUpdating || isLoadingData;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Disciplina
            </h4>
            <p>
              {editMode
                ? "Edite um disciplina do seu sistema"
                : "Cadastre um disciplina no seu sistema"}
            </p>
            <br />

            {!isLoading && (
              <Form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Nome <Attention />
                      </Form.Label>
                      <Form.Control
                        placeholder="Nome"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          validateName(e.target.value);
                        }}
                        isInvalid={!!nameError}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {nameError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/disciplines")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
